import React, { useState, useEffect, useRef } from 'react';
import { Box, MenuItem, FormControl } from '@mui/material';

// Import các component dùng để render giao diện nhập liệu
import BaseFieldControl from './BaseFieldControl';
import JsonObjectControl from './JsonObjectControl'; 
import ObjectArrayControl from './ObjectArrayControl';
import Select from "./ManagedDropdown";

export default function DynamicValueControl({ 
  data, 
  onChange, 
  isReadOnly = false,
  label = "Giá trị",
  defaultType = '' // 1. Nhận thêm thuộc tính kiểu giá trị mặc định (e.g. 'String', 'Code')
}) {
  const [type, setType] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  
  // Dùng ref để kiểm tra xem dữ liệu thay đổi là do người dùng gõ hay do component cha truyền xuống
  const latestInternalValueRef = useRef(currentValue);
  useEffect(() => {
    latestInternalValueRef.current = currentValue;
  }, [currentValue]);

  // ================= 1. XỬ LÝ NHẬN DIỆN KIỂU DỮ LIỆU & GIÁ TRỊ =================
  useEffect(() => {
    // Nếu có dữ liệu mới từ bên ngoài truyền xuống thực sự (không phải do gõ phím)
    const isExternalChange = latestInternalValueRef.current !== data;

    // TỰ ĐỘNG XÁC ĐỊNH KIỂU theo dữ liệu ĐANG CÓ. `defaultType` chỉ là gợi ý cho ô TRỐNG —
    // nếu để defaultType thắng thì một giá trị script đang lưu sẽ bị hiển thị nhầm thành Boolean.
    if (!type) {
      let detectedType = '';
      if (data !== undefined && data !== null) {
        if (Array.isArray(data)) {
          detectedType = 'Array';
        } else if (typeof data === 'number') {
          detectedType = 'Number';
        } else if (typeof data === 'boolean') {
          detectedType = 'Boolean';
        } else if (typeof data === 'object') {
          detectedType = 'Object'; 
        } else if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}/.test(data)) {
          detectedType = 'Date';
        } else if (typeof data === 'string') {
          if (data.includes("return ") || data.includes("{") || data.includes("[")) {
            detectedType = "Code";
          } else {
            detectedType = 'String';
          }
        }
      }
      setType(detectedType || defaultType || '');
    }

    // Cập nhật giá trị hiển thị (Chỉ cập nhật khi dữ liệu từ bên ngoài thực sự thay đổi)
    if (isExternalChange) {
      setCurrentValue(data !== undefined && data !== null ? data : '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultType]); // Theo dõi thêm defaultType để cập nhật kịp thời

  // ================= 2. XỬ LÝ KHI THAY ĐỔI KIỂU DỮ LIỆU (TYPE) =================
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    
    // Tự động reset giá trị về mặc định của kiểu dữ liệu mới để tránh lỗi Crash UI
    let resetValue;
    if (newType === 'Boolean') resetValue = false;
    else if (newType === 'Number') resetValue = 0;
    else if (newType === 'Object') resetValue = {};
    else if (newType === 'Array') resetValue = [];
    else if (newType === 'String' || newType === 'Code') resetValue = "";

    setCurrentValue(resetValue);
    if (onChange) onChange(null, resetValue, newType); 
  };

  // ================= 3. XỬ LÝ KHI THAY ĐỔI GIÁ TRỊ (VALUE) =================
  const handleValueChange = (val) => {
    const finalVal = (val && val.target !== undefined) ? val.target.value : val;
    setCurrentValue(finalVal);
    if (onChange) onChange(null, finalVal, type);
  };

  // ================= 4. RENDER GIAO DIỆN =================
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
      {/* KHU VỰC 1: CHỌN KIỂU DỮ LIỆU */}
      <FormControl size="small" sx={{ width: { xs: '35%', md: '25%' }, flexShrink: 0 }}>
        <Select
          value={type}
          onChange={handleTypeChange}
          disabled={isReadOnly}
          sx={{ bgcolor: isReadOnly ? 'action.hover' : 'background.paper', zIndex: 999999 }}
        >
          <MenuItem value="">--Chọn kiểu giá trị--</MenuItem>
          <MenuItem value="String">Chuỗi (String)</MenuItem>
          <MenuItem value="Number">Số (Number)</MenuItem>
          <MenuItem value="Boolean">Luận lý (Boolean)</MenuItem>
          <MenuItem value="Date">Ngày (Date)</MenuItem>
          <MenuItem value="Object">Đối tượng (JSON)</MenuItem>
          <MenuItem value="Array">Mảng (Array)</MenuItem>
          <MenuItem value="Code">Code</MenuItem>
        </Select>
      </FormControl>

      {/* KHU VỰC 2: RENDER COMPONENT NHẬP LIỆU ĐỘNG */}
      {!!type && <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {type === 'Object' ? (
           <JsonObjectControl 
             data={currentValue}
             onChange={(e, value) => handleValueChange(value)}
             isReadOnly={isReadOnly}
           />
        ) : type === 'Array' ? (
           <ObjectArrayControl 
             field={{ label: label }}
             value={Array.isArray(currentValue) ? currentValue : []}
             onChange={(newArray) => handleValueChange(newArray)}
             formErrors={{}}
             isReadOnly={isReadOnly}
           />
        ) : (
           <BaseFieldControl
             field={{ type: type, label: label }}
             value={currentValue}
             onChange={handleValueChange}
             isReadOnly={isReadOnly}
           />
        )}
      </Box>}
    </Box>
  );
}